/**
 * ADM-02 课程报名区块（组级，enrollment-submission-source-alignment:SC-58，design D12）。
 *
 * 挂载于学生与报名复合列表（StudentListPage）：报名单聚合表格（学生/校区/来源商品/
 * 来源/顾问/课程数/转化进度 n/m 已购）+ 展开单内课程明细（转化状态派生）+ 来源商品
 * 维度筛选 + 代建（携带来源商品）/组级详情（双层，组级编辑与转订单入口）。
 * 提交动作上报 action: enrollment.submit（由 EnrollmentCreateModal 承担）。
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Button,
  Card,
  Descriptions,
  Drawer,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  enrollmentsApi,
  CONVERSION_STATUS_LABEL,
  type EnrollmentConversionStatus,
  type EnrollmentItem,
  type EnrollmentSubmissionDetail,
  type EnrollmentSubmissionItem,
} from "../../api/enrollments.api";
import { productApi, type ProductItem } from "../../api/products.api";
import { EnrollmentCreateModal } from "./EnrollmentCreateModal";
import { fmtDateTime } from "../../utils/datetime";
import { EnrollmentEditModal } from "./EnrollmentEditModal";
import { ConvertOrderModal } from "./ConvertOrderModal";

const CONVERSION_COLOR: Record<EnrollmentConversionStatus, string> = {
  PENDING_TRIAL: "blue",
  IN_TRIAL: "geekblue",
  TRIAL_ENDED_FOLLOW_UP: "orange",
  CONVERTED_PENDING_PAYMENT: "gold",
  CONVERTED_PAID: "green",
};

const SOURCE_LABEL: Record<string, string> = {
  STAFF_ASSISTED: "员工代建",
  PARENT_SELF: "家长自助",
  SHARE_REFERRAL: "好友分享",
};

export interface EnrollmentSectionProps {
  /** 报名列表加载后上报（供复合页聚合学生「报名状态」摘要；单条口径）。 */
  onItemsLoaded?: (items: EnrollmentItem[]) => void;
}

/** 供复合页外部触达的句柄（如工具栏代建提交后同步刷新组列表）。 */
export interface EnrollmentSectionHandle {
  refresh: () => void;
}

/** 展开行内容：按需拉取组级详情渲染组内子报名明细。 */
function GroupApplications({ submissionId }: { submissionId: string }) {
  const [detail, setDetail] = useState<EnrollmentSubmissionDetail | null>(null);
  useEffect(() => {
    enrollmentsApi
      .getEnrollmentSubmission(submissionId)
      .then(setDetail)
      .catch(() => setDetail(null));
  }, [submissionId]);
  if (!detail) return <span>加载中…</span>;
  return (
    <Table<EnrollmentItem>
      scroll={{ x: "max-content" }}
      rowKey="applicationId"
      size="small"
      pagination={false}
      dataSource={detail.applications}
      data-testid={`group-applications-${submissionId.slice(0, 8)}`}
      columns={[
        { title: "课程 SKU", dataIndex: "skuName" },
        { title: "已赠试课", dataIndex: "trialGranted", width: 90 },
        { title: "赠送课剩余", dataIndex: "giftedBalance", width: 90 },
        { title: "正式课剩余", dataIndex: "formalBalance", width: 90 },
        {
          title: "转化状态",
          dataIndex: "conversionStatus",
          render: (v: EnrollmentConversionStatus) => (
            <Tag color={CONVERSION_COLOR[v]}>{CONVERSION_STATUS_LABEL[v]}</Tag>
          ),
        },
      ]}
    />
  );
}

/** 组级详情抽屉（双层：组 + 组内子报名；组级编辑/转订单入口，SC-12/SC-58）。 */
function GroupDetailDrawer({
  submissionId,
  onClose,
  onChanged,
}: {
  submissionId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<EnrollmentSubmissionDetail | null>(null);
  const [editItem, setEditItem] = useState<EnrollmentItem | null>(null);
  const [convertOpen, setConvertOpen] = useState(false);

  useEffect(() => {
    enrollmentsApi
      .getEnrollmentSubmission(submissionId)
      .then(setDetail)
      .catch(() => setDetail(null));
  }, [submissionId]);

  return (
    <Drawer
      title={`报名单详情${detail ? ` · ${detail.studentName}` : ""}`}
      open
      onClose={onClose}
      width={560}
      extra={
        detail && (
          <Space>
            <Button
              type="primary"
              ghost
              onClick={() => setEditItem(detail.applications[0] ?? null)}
              data-testid="group-edit-btn"
            >
              编辑（整单）
            </Button>
            <Button
              onClick={() => setConvertOpen(true)}
              data-testid="group-convert-btn"
            >
              转订单
            </Button>
          </Space>
        )
      }
    >
      {detail && (
        <>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="报名单号">
              <Typography.Text copyable={{ text: detail.submissionId }}>
                #{detail.submissionId.slice(0, 8)}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="学生">
              {detail.studentName}
            </Descriptions.Item>
            <Descriptions.Item label="校区">
              {detail.campusName}
            </Descriptions.Item>
            <Descriptions.Item label="来源商品">
              {detail.sourceProductName ?? "—（无来源商品）"}
            </Descriptions.Item>
            <Descriptions.Item label="来源">
              {SOURCE_LABEL[detail.source] ?? detail.source}
            </Descriptions.Item>
            <Descriptions.Item label="顾问">
              {detail.consultantName ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="转化进度">
              {detail.conversionProgress}
            </Descriptions.Item>
            <Descriptions.Item label="备注">
              {detail.note ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="提交时间">
              {fmtDateTime(detail.submittedAt)}
            </Descriptions.Item>
          </Descriptions>
          <Table<EnrollmentItem>
            scroll={{ x: "max-content" }}
            rowKey="applicationId"
            size="small"
            style={{ marginTop: 12 }}
            pagination={false}
            dataSource={detail.applications}
            columns={[
              { title: "课程 SKU", dataIndex: "skuName" },
              { title: "已赠试课", dataIndex: "trialGranted", width: 80 },
              { title: "赠送课剩余", dataIndex: "giftedBalance", width: 80 },
              {
                title: "转化状态",
                dataIndex: "conversionStatus",
                render: (v: EnrollmentConversionStatus) => (
                  <Tag color={CONVERSION_COLOR[v]}>
                    {CONVERSION_STATUS_LABEL[v]}
                  </Tag>
                ),
              },
            ]}
          />
          {editItem && (
            <EnrollmentEditModal
              open
              item={editItem}
              onClose={() => setEditItem(null)}
              onSaved={() => {
                setEditItem(null);
                enrollmentsApi
                  .getEnrollmentSubmission(submissionId)
                  .then(setDetail);
                onChanged();
              }}
            />
          )}
          <ConvertOrderModal
            open={convertOpen}
            submissionId={submissionId}
            onClose={() => setConvertOpen(false)}
          />
        </>
      )}
    </Drawer>
  );
}

export const EnrollmentSection = forwardRef<
  EnrollmentSectionHandle,
  EnrollmentSectionProps
>(function EnrollmentSection({ onItemsLoaded }, ref) {
  const [data, setData] = useState<EnrollmentSubmissionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [sourceProductId, setSourceProductId] = useState<string | undefined>();
  const [keyword, setKeyword] = useState<string | undefined>();
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  useEffect(() => {
    // 来源商品筛选选项（三维度之一，SC-30）
    productApi
      .listProducts({ page: 1, pageSize: 100, status: "ON_SHELF" })
      .then((r) => setProducts(r.items))
      .catch(() => setProducts([]));
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      // 组级聚合展示（主表）+ 单条口径并行拉取（复合页学生报名摘要）
      const [groups, singles] = await Promise.all([
        enrollmentsApi.listEnrollmentSubmissions({
          sourceProductId,
          keyword,
          page,
          pageSize,
        }),
        enrollmentsApi.listEnrollments({ keyword, page, pageSize }),
      ]);
      setData(groups.items);
      setTotal(groups.total);
      onItemsLoaded?.(singles.items);
    } finally {
      setLoading(false);
    }
  }, [sourceProductId, keyword, page, pageSize]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  // 外部刷新句柄：工具栏代建入口提交后由复合页调用，同步刷新组列表
  useImperativeHandle(ref, () => ({ refresh: () => void fetchList() }), [
    fetchList,
  ]);

  const columns: ColumnsType<EnrollmentSubmissionItem> = [
    {
      title: "报名单号",
      dataIndex: "submissionId",
      width: 140,
      ellipsis: true,
      render: (v: string) => (
        <Tooltip title={v}>
          <Button type="link" size="small" onClick={() => setDetailId(v)}>
            #{v.slice(0, 8)}
          </Button>
        </Tooltip>
      ),
    },
    { title: "学生", dataIndex: "studentName" },
    { title: "校区", dataIndex: "campusName" },
    {
      title: "来源商品",
      dataIndex: "sourceProductName",
      ellipsis: true,
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "来源",
      dataIndex: "source",
      render: (v: string) => SOURCE_LABEL[v] ?? v,
    },
    { title: "顾问", dataIndex: "consultantName", render: (v) => v ?? "—" },
    {
      title: "课程数",
      dataIndex: "applicationCount",
      width: 90,
      render: (v: number) => `${v} 门`,
    },
    {
      title: "转化进度",
      dataIndex: "conversionProgress",
      width: 110,
      render: (v: string, row) => (
        <Tag
          color={row.convertedCount === row.applicationCount ? "green" : "blue"}
        >
          {v}
        </Tag>
      ),
    },
    {
      title: "提交时间",
      dataIndex: "submittedAt",
      render: (v: string) => fmtDateTime(v),
    },
    {
      title: "备注",
      dataIndex: "note",
      ellipsis: true,
      render: (v) => v ?? "—",
    },
  ];

  return (
    <Card
      title="课程报名"
      style={{ marginTop: 16 }}
      data-testid="enrollment-section"
    >
      <Space style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          onClick={() => setCreateOpen(true)}
          data-testid="enrollment-create-btn"
        >
          代家长报名
        </Button>
        <Select
          placeholder="来源商品"
          allowClear
          value={sourceProductId}
          onChange={(v) => {
            setSourceProductId(v);
            setPage(1);
          }}
          style={{ width: 220 }}
          options={products.map((p) => ({ value: p.id, label: p.name }))}
          data-testid="enrollment-filter-product"
        />
        <Input.Search
          placeholder="学生姓名 / 学号 / 监护人手机号"
          allowClear
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value || undefined);
            setPage(1);
          }}
          onSearch={(v) => {
            setKeyword(v || undefined);
            setPage(1);
          }}
          style={{ width: 240 }}
          data-testid="enrollment-filter-keyword"
        />
      </Space>
      <Table<EnrollmentSubmissionItem>
        scroll={{ x: "max-content" }}
        rowKey="submissionId"
        columns={columns}
        dataSource={data}
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <GroupApplications submissionId={record.submissionId} />
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
      <EnrollmentCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmitted={() => void fetchList()}
      />
      {detailId && (
        <GroupDetailDrawer
          submissionId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => void fetchList()}
        />
      )}
    </Card>
  );
});
